import { CONTACT_EMAIL, CONTROLLER_NAME, LegalDocument } from './legalDocument';

export const privacyPolicy: LegalDocument = {
  title: 'Política de Privacidade',
  updated: '3 de novembro de 2026',
  intro: [
    'Esta política explica quais dados o Financy coleta, para que usa, com quem compartilha, por quanto tempo guarda e como você exerce os seus direitos. Ela vale para o site financybr.com, o painel e o Fin no Telegram.',
  ],
  sections: [
    {
      title: 'Quem é o responsável pelos seus dados',
      paragraphs: [
        `O Financy é mantido por **${CONTROLLER_NAME}**, responsável (controlador) pelos dados tratados no serviço, nos termos da Lei Geral de Proteção de Dados (Lei nº 13.709/2018, a LGPD).`,
        `Para qualquer assunto sobre privacidade, escreva para **${CONTACT_EMAIL}**.`,
      ],
    },
    {
      title: 'Quais dados coletamos',
      items: [
        '**Dados da conta:** nome, sobrenome, e-mail e senha (guardada de forma criptografada, nunca em texto). Também o idioma, o fuso horário e a moeda que você prefere.',
        '**Dados do Telegram:** quando você conecta, o número que identifica a sua conta no Telegram, o seu nome de usuário e o nome que aparece no Telegram. O Fin não vê as suas outras conversas: ele só recebe o que você manda para ele ou nos grupos em que ele está.',
        '**O que você manda para o Fin:** textos, áudios, fotos e PDFs de recibos. O texto das mensagens fica guardado junto com o lançamento que ele gerou. Áudios, fotos e PDFs são lidos e não ficam guardados.',
        '**Seus registros financeiros:** lançamentos (valor, descrição, categoria, data e estabelecimento), contas a pagar, metas, grupos, divisões e acertos.',
        '**Dados de uso:** datas de cadastro, de último acesso e de último uso (para a regra de inatividade abaixo) e registros técnicos de erro.',
        '**Convites:** quando alguém convida para um grupo uma pessoa que ainda não tem conta, guardamos o e-mail dela para mandar o convite.',
      ],
      after: [
        'Não pedimos nem guardamos senha de banco, dados de cartão, acesso a contas bancárias, a sua localização ou os seus contatos.',
      ],
    },
    {
      title: 'Para que usamos',
      items: [
        'Registrar e organizar os gastos e as receitas que você conta ao Fin, e mostrar tudo no painel.',
        'Entender mensagens, áudios e recibos com a ajuda de inteligência artificial e sugerir valor, categoria e data. Você confere e confirma antes de salvar.',
        'Calcular divisões, saldos e acertos nos grupos.',
        'Enviar lembretes de contas e metas, resumos e avisos sobre a sua conta, como o aviso de exclusão por inatividade.',
        'Manter o serviço seguro e funcionando, e corrigir erros.',
      ],
      after: ['Não vendemos os seus dados, não usamos para publicidade e não montamos perfis para terceiros.'],
    },
    {
      title: 'Com que base legal',
      items: [
        '**Execução de contrato** (art. 7º, V, da LGPD): tratar os seus dados é necessário para prestar o serviço que você pediu ao criar a conta.',
        '**Legítimo interesse** (art. 7º, IX): segurança, prevenção de abuso e melhoria do serviço, dentro do que você esperaria.',
        '**Cumprimento de obrigação legal** (art. 7º, II): quando a lei exigir guardar ou fornecer algum dado.',
      ],
    },
    {
      title: 'Com quem compartilhamos',
      paragraphs: ['Alguns serviços de terceiros fazem o Financy funcionar. Cada um recebe só o necessário para a sua tarefa:'],
      items: [
        '**Telegram:** é por onde você conversa com o Fin. As mensagens passam pelos servidores do Telegram, que segue a própria política de privacidade.',
        '**OpenRouter e os provedores de inteligência artificial que ele aciona** (como Google, OpenAI, DeepSeek, Alibaba/Qwen e Groq): recebem o texto, o áudio ou a imagem que você manda para o Fin, para identificar valor, categoria e data.',
        '**Railway:** hospeda os servidores e o banco de dados do Financy.',
        '**Resend:** envia os nossos e-mails, como convites e avisos sobre a conta.',
        '**Sentry:** quando ativado, recebe relatórios de erro para corrigirmos falhas.',
      ],
      after: [
        'Para converter moedas, consultamos cotações num serviço de câmbio, sem enviar nenhum dado seu.',
        'Também podemos compartilhar dados quando a lei ou uma autoridade competente exigir.',
      ],
    },
    {
      title: 'Grupos: quem vê o quê',
      items: [
        'O que você manda no privado para o Fin vai para o seu pessoal. Ninguém mais vê.',
        'Num grupo, os participantes veem os gastos do grupo: descrição, valor, quem pagou, como foi dividido, os saldos, os acertos e o nome de quem participa.',
        'As mensagens que você manda num grupo do Telegram são vistas por todos do grupo, como qualquer conversa no Telegram.',
        'A sua parte dos gastos do grupo aparece no seu pessoal. Os outros participantes não veem o seu pessoal.',
      ],
    },
    {
      title: 'Transferência para outros países',
      paragraphs: [
        'Alguns desses serviços ficam fora do Brasil, principalmente nos Estados Unidos. Por isso, os seus dados podem ser tratados em outros países, sempre para as finalidades desta política e com fornecedores que adotam medidas de segurança e proteção de dados.',
      ],
    },
    {
      title: 'Por quanto tempo guardamos',
      items: [
        'Enquanto você usa o Financy, os seus dados ficam guardados para o serviço funcionar.',
        '**Se você ficar 6 meses sem usar** (nem o site, nem o Fin no Telegram), a sua conta é apagada. Avisamos por e-mail e pelo Telegram 30 dias antes, e qualquer uso nesse período mantém a conta.',
        '**Se você excluir a sua conta**, os dados são apagados na hora.',
        '**O e-mail de quem foi convidado e ainda não tem conta** é apagado quando o convite é aceito ou cancelado, ou 30 dias depois de o convite expirar (ele vale 7 dias).',
        'Nos grupos com outras pessoas, os gastos em que você participou continuam, para os saldos delas fecharem, mas sem nada que identifique você: o seu nome vira “Ex-participante”, e o texto original das suas mensagens e as notas e os endereços que você escreveu são apagados.',
        'Cópias de segurança do banco de dados são guardadas por até 30 dias e depois descartadas. Um dado apagado pode continuar numa cópia até ela ser descartada.',
        'Quando a lei exigir, podemos guardar algum dado por mais tempo, só para cumprir essa obrigação.',
      ],
    },
    {
      title: 'Os seus direitos',
      paragraphs: ['Pela LGPD (art. 18), você pode:'],
      items: [
        'confirmar se tratamos os seus dados e ter acesso a eles;',
        'corrigir dados incompletos ou errados (a maior parte dá para corrigir no próprio app, em Ajustes e nos lançamentos);',
        'excluir a sua conta, na hora, em **Ajustes → Excluir conta**;',
        'pedir uma cópia dos seus dados num formato que dê para levar a outro serviço (portabilidade);',
        'saber com quem compartilhamos os seus dados;',
        'pedir a anonimização, o bloqueio ou a eliminação de dados desnecessários ou tratados em desacordo com a lei;',
        'revogar o consentimento, quando ele for a base do tratamento;',
        'reclamar à Autoridade Nacional de Proteção de Dados (ANPD).',
      ],
      after: [`Para pedidos que o app ainda não faz sozinho, escreva para **${CONTACT_EMAIL}**. Respondemos em até 15 dias.`],
    },
    {
      title: 'Segurança',
      paragraphs: [
        'As senhas são guardadas criptografadas, a comunicação com o site e com o Telegram usa conexão segura (HTTPS) e o acesso ao banco de dados é restrito.',
        'Nenhum sistema é totalmente seguro. Se acontecer um incidente que possa trazer risco ou dano relevante a você, avisaremos você e a ANPD, como manda a lei.',
      ],
    },
    {
      title: 'Menores de idade',
      paragraphs: [
        'O Financy é para maiores de 18 anos. Não coletamos dados de menores de propósito; se soubermos que isso aconteceu, apagamos a conta.',
      ],
    },
    {
      title: 'Mudanças nesta política',
      paragraphs: [
        'Se mudarmos algo importante, avisamos por e-mail ou no app antes de a mudança valer. A data no topo mostra a última atualização.',
      ],
    },
    {
      title: 'Contato',
      paragraphs: [`${CONTROLLER_NAME} · **${CONTACT_EMAIL}**`],
    },
  ],
};
